import { rootLinks } from '@map/components/app/links';
import { useUpdateAuthenticationFlow } from '@map/hooks/flows/authentication';
import { AuthProvider } from '@shared/hooks/auth/providers';
import useAuthentication from '@shared/hooks/auth/useAuthentication';
import { useCurrentLocale, useIntlToString } from '@shared/hooks/intl';
import LoadingIndicator from '@ui/LoadingIndicator';
import SocialCircleButton from '@ui/mobile/SocialButtons/SocialCircleButton';
import SocialPillButton from '@ui/mobile/SocialButtons/SocialPillButton';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

type Props = {
  label: 'signup_with' | 'signin_with';
  icon?: boolean;
  provider: AuthProvider;
};

const SocialAuthButton = ({ provider, icon, label: labelType }: Props) => {
  const { info, onSignIn, onClick } = useAuthentication(provider);
  const { Icon, labelIntl, signInIntl, signUpIntl } = info;
  const intlToString = useIntlToString();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const updateAuthenticationFlow = useUpdateAuthenticationFlow();
  const locale = useCurrentLocale();

  const handleSignIn = async () => {
    try {
      if (onClick) {
        onClick();
        return;
      }
      /* istanbul ignore if */
      if (!onSignIn) {
        return;
      }
      setIsLoading(true);
      const { success, user } = await onSignIn(locale);

      if (!success) {
        return;
      }

      if (user?.account_status === 'complete') {
        updateAuthenticationFlow({
          method: provider === 'google_oauth2' ? 'google' : provider,
          action: user.is_new_user ? 'register' : 'sign_in',
        });

        history.push(rootLinks.inner());
      }
    } catch (ex) {
      setIsLoading(false);
      // ignore this error, emitted by Google SSO
      if (!ex || (ex as any).type === 'popup_closed') {
        return;
      }
      console.warn(ex);
    }
  };

  const iconElement = Icon ? <Icon /> : undefined;

  const labelText = useMemo(() => {
    const provider = labelIntl ? intlToString(labelIntl) : info.label;
    if (labelType === 'signup_with') {
      return signUpIntl ?? <FormattedMessage id="login.sign_up_with" values={{ provider }} />;
    }
    return signInIntl ?? <FormattedMessage id="login.log_in_with" values={{ provider }} />;
  }, [info.label, intlToString, labelType, labelIntl, signInIntl, signUpIntl]);

  return (
    <>
      {isLoading && <LoadingIndicator />}
      {icon && iconElement ? (
        <SocialCircleButton
          label={labelText}
          icon={iconElement}
          onClick={handleSignIn}
          provider={info.label}
        />
      ) : (
        <SocialPillButton label={labelText} icon={iconElement} onClick={handleSignIn} />
      )}
    </>
  );
};

export default SocialAuthButton;
